/**
 * Thin wrapper around fetch for the busch-erp API.
 * Reads CSRF from the busch_erp_csrf cookie and sends it as X-CSRF-Token
 * on non-GET requests (matches backend csrfPlugin).
 */

function getCookie(name: string): string | null {
  const parts = document.cookie.split(';').map((c) => c.trim());
  for (const part of parts) {
    if (part.startsWith(name + '=')) return decodeURIComponent(part.slice(name.length + 1));
  }
  return null;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiOptions<T = unknown> {
  method?: 'GET' | 'HEAD' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /**
   * Optionaler Runtime-Boundary-Check. Wenn gesetzt, wird die JSON-Antwort
   * durch `parse` validiert statt blind nach `T` gecastet. Wirft `parse`,
   * blubbert der Fehler hoch wie ein Netzwerk-/HTTP-Fehler — die React-
   * Komponente sieht ihn am Query-Boundary statt tief in der Render-Pipe.
   *
   * Aufrufer können hier eine handgerollte Type-Guard-Funktion hinterlegen
   * oder — sobald `zod` FE-Dep ist — `Schema.parse` direkt einsetzen
   * (T-013 / F-602; volle Migration kritischer Pfade gehört zu T-014).
   */
  parse?: (raw: unknown) => T;
}

export async function api<T>(path: string, opts: ApiOptions<T> = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  // HEAD ist idempotent und body-los — Fastify-csrfPlugin erwartet kein Token.
  if (method !== 'GET' && method !== 'HEAD') {
    const csrf = getCookie('busch_erp_csrf');
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }

  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers,
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
    ...(opts.signal ? { signal: opts.signal } : {}),
  });

  const contentType = res.headers.get('content-type') ?? '';
  // HEAD-Antworten haben per RFC keinen Body — res.json() wuerde mit
  // "Unexpected end of JSON input" werfen, also gar nicht erst versuchen.
  const hasJson = method !== 'HEAD' && contentType.includes('application/json');
  const payload: unknown = hasJson ? await res.json() : null;

  if (!res.ok) {
    const err = (payload as { error?: { code?: string; message?: string; requestId?: string } })
      ?.error;
    throw new ApiError(
      res.status,
      err?.code ?? 'HTTP_' + res.status,
      err?.message ?? res.statusText,
      err?.requestId,
    );
  }
  return opts.parse ? opts.parse(payload) : (payload as T);
}
